import apiClient from "./apiClient";

export const ALL_SCOPE_VALUE = "All";
export const ALL_DATES_FILTER_OPTION = "all";
export const REPORT_FILTER_OPTION_STORAGE_KEY = "filter_option";
export const REPORT_SCOPE_STORAGE_KEY = "selectedPoliceStation";
export const REPORT_SUMMARY_SCOPE_STORAGE_KEY = "selectedPoliceStations";
export const REPORT_HIERARCHY_SCOPE_STORAGE_KEY = "reportHierarchyScope";
const LEGACY_ALL_DATES_FILTER_OPTION = "all_dates";

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeValue = (value) => String(value ?? "").trim();
export const normalizeDateFilterOption = (value, fallback = "") => {
  const normalizedValue = normalizeValue(value);

  if (!normalizedValue) {
    return fallback;
  }

  if (normalizedValue === LEGACY_ALL_DATES_FILTER_OPTION) {
    return ALL_DATES_FILTER_OPTION;
  }

  return normalizedValue;
};
const withQueryString = (path, params) => {
  const query =
    params instanceof URLSearchParams
      ? params.toString()
      : new URLSearchParams(params).toString();

  return query ? `${path}?${query}` : path;
};

const toResponseData = (response) => response?.data ?? {};
const hasExplicitDateRange = (params) =>
  params.has("start_date") && params.has("end_date");
const hasQuickFilter = (params) => params.has("filter_option");
const hasCustomDateRange = (filters = {}) =>
  Boolean(formatDateForApi(filters.startDate) && formatDateForApi(filters.endDate));

export const hasReportPermission = (permissions, action) =>
  toArray(permissions?.reports).includes(action);

export const buildHierarchyParams = (scope = {}) => {
  const params = new URLSearchParams();
  const stationId = normalizeValue(scope.stationId ?? scope.station_id);
  const subDivisionId = normalizeValue(
    scope.subDivisionId ?? scope.sub_division_id
  );
  const districtId = normalizeValue(scope.districtId ?? scope.district_id);
  const userIds = toArray(
    scope.userIds ??
      scope.user_ids ??
      (scope.userId ?? scope.user_id ? [scope.userId ?? scope.user_id] : [])
  ).filter(Boolean);

  if (districtId && districtId !== ALL_SCOPE_VALUE) {
    params.append("district_id", districtId);
  }

  if (subDivisionId && subDivisionId !== ALL_SCOPE_VALUE) {
    params.append("sub_division_id", subDivisionId);
  }

  if (stationId && stationId !== ALL_SCOPE_VALUE) {
    params.append("station_id", stationId);
  }

  userIds.forEach((userId) => {
    params.append("user_ids[]", userId);
  });

  return params;
};

const formatDateForApi = (value) => {
  if (!value) {
    return "";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

export const appendDateParams = (params, filters = {}) => {
  const nextParams = params instanceof URLSearchParams ? params : new URLSearchParams();
  const filterOption = normalizeDateFilterOption(filters.filterOption);
  const startDate = formatDateForApi(filters.startDate);
  const endDate = formatDateForApi(filters.endDate);

  if (filterOption && filterOption !== "custom") {
    nextParams.set("filter_option", filterOption);
    return nextParams;
  }

  if (startDate && endDate) {
    nextParams.set("start_date", startDate);
    nextParams.set("end_date", endDate);
  }

  return nextParams;
};

export const hasActiveDateFilter = (filters = {}) => {
  const filterOption = normalizeDateFilterOption(filters.filterOption);

  if (filterOption && filterOption !== "custom") {
    return true;
  }

  return hasCustomDateRange(filters);
};

export const extractReportSummary = (payload) => ({
  reportData:
    payload?.total?.report_data ??
    payload?.total_report_data ??
    payload?.report_data ??
    {},
  statusData:
    payload?.total?.status_data ??
    payload?.total_status_data ??
    payload?.status_data ??
    {},
});

export const extractScopedList = (payload) => {
  if (payload?.success === false) {
    return [];
  }

  return toArray(payload?.data);
};

export const extractUserAccessUserIds = (payload) =>
  Array.from(
    new Set(
      extractScopedList(payload)
        .map((row) => row?.user_id)
        .filter(Boolean)
    )
  );

export const fetchPersonalDashboardSummary = async () => {
  const [reportResponse, statusResponse] = await Promise.all([
    apiClient.get("/api/report"),
    apiClient.get("/api/status"),
  ]);

  return {
    reportData: toResponseData(reportResponse),
    statusData: toResponseData(statusResponse),
  };
};

export const fetchPersonalRecords = async () => {
  const response = await apiClient.get("/api/data");
  const data = toResponseData(response);

  return Array.isArray(data) ? data : toArray(data?.data);
};

export const fetchAdminDashboardSummary = async (scope = {}, filters = {}) => {
  const params = appendDateParams(buildHierarchyParams(scope), filters);
  let path = "/api/admin/user_all_report_data";

  if (hasQuickFilter(params)) {
    if (params.get("filter_option") === "last_month") {
      params.delete("filter_option");
      path = "/api/admin/user_report_data_last_month";
    } else {
      path = "/api/admin/user_report_data_by_filter";
    }
  } else if (hasExplicitDateRange(params)) {
    path = "/api/admin/user_report_data_by_date";
  }

  const response = await apiClient.get(withQueryString(path, params));
  return extractReportSummary(toResponseData(response));
};

export const fetchCasePropertyCount = async (scope = {}) => {
  const response = await apiClient.get(
    withQueryString(
      "/api/admin/v2/data/case_property_count",
      buildHierarchyParams(scope)
    )
  );
  const data = toResponseData(response);

  if (data?.success === false) {
    return {};
  }

  return data;
};

export const fetchEntryTypeCounts = async ({
  category = "",
  filters = {},
  lang = "",
  scope = {},
} = {}) => {
  const params = appendDateParams(buildHierarchyParams(scope), filters);
  const normalizedCategory = normalizeValue(category);
  const normalizedLang = normalizeValue(lang);

  if (normalizedCategory) {
    params.set("category", normalizedCategory);
  }

  if (normalizedLang) {
    params.set("lang", normalizedLang);
  }

  const response = await apiClient.get(
    withQueryString("/api/reports/entry_type/counts", params)
  );
  const data = toResponseData(response);

  if (data?.success === false) {
    return [];
  }

  return toArray(data?.counts);
};

// Backup helper only. Active dashboard/report flows should use capability scope
// plus hierarchy/user dropdown filters instead of admin/user-access.
export const fetchAdminUserAccess = async (scope = {}) => {
  const response = await apiClient.get(
    withQueryString("/api/admin/user-access", buildHierarchyParams(scope))
  );

  return extractScopedList(toResponseData(response));
};

export const fetchCrimeBranchRecords = async (scope = {}) => {
  const response = await apiClient.get(
    withQueryString("/api/admin/data/crime_branch", buildHierarchyParams(scope))
  );

  return extractScopedList(toResponseData(response));
};

export const fetchFilteredCrimeRecords = async (scope = {}, filters = {}) => {
  const response = await apiClient.get(
    withQueryString(
      "/api/admin/v2/data/crime_data",
      appendDateParams(buildHierarchyParams(scope), filters)
    )
  );

  return extractScopedList(toResponseData(response));
};

export const exportCrimeData = async ({
  category = "",
  entryType = "",
  filters = {},
  format = "xlsx",
  lang = "",
  scope = {},
  search = "",
  status = "",
  vehicleReason = "",
} = {}) => {
  const params = appendDateParams(buildHierarchyParams(scope), filters);
  const normalizedFormat = normalizeValue(format) || "xlsx";
  const normalizedLang = normalizeValue(lang);
  const normalizedSearch = normalizeValue(search);
  const normalizedStatus = normalizeValue(status);
  const normalizedCategory = normalizeValue(category);
  const normalizedEntryType = normalizeValue(entryType);
  const normalizedVehicleReason = normalizeValue(vehicleReason);

  params.set("format", normalizedFormat);

  if (normalizedLang) {
    params.set("lang", normalizedLang);
  }

  if (normalizedSearch) {
    params.set("search", normalizedSearch);
  }

  if (normalizedStatus) {
    params.set("status", normalizedStatus);
  }

  if (normalizedCategory) {
    params.set("category", normalizedCategory);
  }

  if (normalizedEntryType) {
    params.set("entry_type", normalizedEntryType);
  }

  if (normalizedVehicleReason) {
    params.set("vehicle_reason", normalizedVehicleReason);
  }

  return apiClient.get(withQueryString("/api/data/export", params), {
    responseType: "blob",
  });
};

const REPORT_ENDPOINTS = {
  muddamaal_type: {
    admin: "/api/admin/data/category",
    adminFiltered: "/api/admin/v2/data/category",
    personal: "/api/data/category",
  },
  status_of_muddamaal: {
    admin: "/api/admin/data/status",
    adminFiltered: "/api/admin/v2/data/status",
    personal: "/api/data/status",
  },
  vehicle_reason: {
    admin: "/api/admin/data/vehicle",
    adminFiltered: "/api/admin/v2/data/vehicle",
    personal: "/api/data/vehicle",
  },
};

export const fetchReportRecords = async ({
  canReadAdmin = false,
  categoryName = "",
  categoryType = "",
  filters = {},
  scope = {},
}) => {
  const endpointGroup = REPORT_ENDPOINTS[categoryType];

  if (!endpointGroup) {
    return [];
  }

  const encodedCategoryName = encodeURIComponent(categoryName);

  if (canReadAdmin) {
    const params = appendDateParams(buildHierarchyParams(scope), filters);
    const endpoint =
      hasQuickFilter(params) || hasExplicitDateRange(params)
        ? endpointGroup.adminFiltered
        : endpointGroup.admin;
    const response = await apiClient.get(
      withQueryString(`${endpoint}/${encodedCategoryName}`, params)
    );

    return extractScopedList(toResponseData(response));
  }

  const response = await apiClient.get(
    `${endpointGroup.personal}/${encodedCategoryName}`
  );
  const data = toResponseData(response);

  return extractScopedList(data?.data ? data : { data });
};

export const fetchEntryTypeRecords = async ({
  category = "",
  entryType = "",
  filters = {},
  lang = "",
  page = 1,
  pageSize = 50,
  scope = {},
  search = "",
  sortBy = "",
  sortDir = "desc",
} = {}) => {
  const normalizedEntryType = normalizeValue(entryType) || "none";
  const params = appendDateParams(buildHierarchyParams(scope), filters);
  const normalizedCategory = normalizeValue(category);
  const normalizedLang = normalizeValue(lang);
  const normalizedSearch = normalizeValue(search);
  const normalizedSortBy = normalizeValue(sortBy);
  const normalizedSortDir = normalizeValue(sortDir);
  const normalizedPage = Math.max(1, Number(page) || 1);
  const normalizedPageSize = Math.min(
    100,
    Math.max(1, Number(pageSize) || 50)
  );

  params.set("page", normalizedPage);
  params.set("page_size", normalizedPageSize);

  if (normalizedCategory) {
    params.set("category", normalizedCategory);
  }

  if (normalizedLang) {
    params.set("lang", normalizedLang);
  }

  if (normalizedSearch) {
    params.set("search", normalizedSearch);
  }

  if (normalizedSortBy) {
    params.set("sort_by", normalizedSortBy);
  }

  if (normalizedSortDir) {
    params.set("sort_dir", normalizedSortDir);
  }

  const response = await apiClient.get(
    withQueryString(
      `/api/reports/entry_type/${encodeURIComponent(
        normalizedEntryType
      )}/records`,
      params
    )
  );
  const data = toResponseData(response);

  if (data?.success === false) {
    return { data: [], total: 0 };
  }

  const records = toArray(data?.data ?? data?.records);
  const total = Number(
    data?.total ??
      data?.pagination?.total ??
      data?.meta?.total ??
      records.length
  );

  return {
    data: records,
    total: Number.isFinite(total) ? total : records.length,
  };
};
